import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ScrollText, CheckCircle2, Users, MessageCircle, Scale, Mail, Youtube } from "lucide-react";
import useSeo from "@/hooks/useSeo";
import { LogoMark } from "@/components/Logo";
import { CONTACT_INFO } from "@/lib/constants";

const PRINCIPLES = [
  "Independencia editorial", "Rigor periodístico", "Verificación de información", "Pluralidad",
  "Libertad de expresión", "Libertad de conciencia", "Respeto por las diferencias", "No discriminación",
  "Transparencia", "Derecho a respuesta", "Corrección de errores", "Diferenciación entre información y opinión",
];

export default function QuienesSomos() {
  useSeo({ title: "Quiénes somos", description: "NEXODUM es un medio de comunicación independiente dedicado al periodismo, la información y la producción de contenidos." });

  return (
    <div data-testid="quienes-somos-page">
      <section className="bg-nx-dark text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{ background: "radial-gradient(circle at 75% 25%, #0055FF 0%, transparent 45%), radial-gradient(circle at 15% 80%, #FF6B35 0%, transparent 40%)" }} />
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 relative">
          <LogoMark size={72} />
          <h1 className="font-serif font-black text-4xl md:text-6xl mt-6 tracking-tight">Quiénes somos</h1>
          <p className="mt-5 text-slate-300 text-base md:text-lg max-w-3xl leading-relaxed">
            NEXODUM es un medio de comunicación independiente dedicado al periodismo, la información y la producción de contenidos sobre sociedad, culturas, religiones, comunidades, colectividades, historia y actualidad.
          </p>
          <p className="mt-4 text-slate-400 text-sm md:text-base max-w-3xl leading-relaxed">
            Nace con el propósito de contar historias, investigar realidades y generar espacios de conocimiento y diálogo sobre las distintas formas en que las personas y las comunidades construyen su identidad, sus creencias, sus tradiciones y su vida en sociedad.
          </p>
        </div>
      </section>

      <section className="max-w-container mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <div className="flex items-center gap-3 mb-4"><ShieldCheck className="text-nx-orange" size={26} /><h2 className="font-serif font-black text-2xl md:text-3xl text-nx-dark">Independencia editorial</h2></div>
            <p className="text-gray-600 leading-relaxed">
              NEXODUM no constituye un órgano de representación de ninguna religión, iglesia, comunidad religiosa, colectividad, partido político, organismo estatal, organización social o institución particular.
            </p>
            <p className="text-gray-600 leading-relaxed mt-4">
              La religión y las colectividades son parte de nuestra cobertura, pero también informamos sobre sociedad, actualidad, cultura, conflictos, migraciones, derechos, historia y acontecimientos internacionales.
            </p>
          </div>
          <div className="bg-nx-soft rounded-md p-8">
            <div className="grid grid-cols-2 gap-6 text-center">
              {[{ i: ScrollText, t: "Periodismo" }, { i: Users, t: "Pluralidad" }, { i: MessageCircle, t: "Diálogo" }, { i: Scale, t: "Rigor" }].map((v, k) => {
                const I = v.i; return (
                  <div key={k} className="flex flex-col items-center gap-2">
                    <I className="text-nx-blue" size={30} strokeWidth={1.5} />
                    <span className="text-sm font-bold text-nx-dark">{v.t}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-nx-soft py-16">
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif font-black text-2xl md:text-3xl text-nx-dark mb-8">Nuestros principios</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {PRINCIPLES.map((p, i) => (
              <div key={i} className="flex items-center gap-3 bg-white border border-gray-200 rounded-md px-4 py-3">
                <CheckCircle2 size={18} className="text-nx-orange shrink-0" />
                <span className="text-sm font-medium text-nx-dark">{p}</span>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/contacto" data-testid="quienes-contacto-cta" className="inline-block nx-gradient-bg text-white font-bold px-8 py-3 rounded-full text-sm uppercase tracking-wider hover:opacity-90 transition-opacity">
              Contactá a la redacción
            </Link>
          </div>
        </div>
      </section>

      {/* Contacto institucional */}
      <section className="max-w-container mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="institucional-contacto">
        <h2 className="font-serif font-black text-2xl md:text-3xl text-nx-dark mb-8">Contacto institucional</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl">
          <a href={`mailto:${CONTACT_INFO.email}`} data-testid="institucional-email"
            className="group flex items-center gap-4 border border-gray-200 rounded-md p-5 hover:border-nx-orange transition-colors">
            <span className="w-12 h-12 rounded-md flex items-center justify-center bg-nx-orange/10 shrink-0"><Mail className="text-nx-orange" size={22} /></span>
            <div>
              <p className="text-[11px] uppercase tracking-widest text-gray-400">Email</p>
              <p className="font-bold text-nx-dark group-hover:text-nx-orange transition-colors break-all">{CONTACT_INFO.email}</p>
            </div>
          </a>
          <a href={CONTACT_INFO.youtube} target="_blank" rel="noopener noreferrer" data-testid="institucional-youtube"
            className="group flex items-center gap-4 border border-gray-200 rounded-md p-5 hover:border-nx-blue transition-colors">
            <span className="w-12 h-12 rounded-md flex items-center justify-center bg-nx-blue/10 shrink-0"><Youtube className="text-nx-blue" size={22} /></span>
            <div>
              <p className="text-[11px] uppercase tracking-widest text-gray-400">Canal de YouTube</p>
              <p className="font-bold text-nx-dark group-hover:text-nx-blue transition-colors">@NexodumNoticias</p>
            </div>
          </a>
        </div>
      </section>
    </div>
  );
}
